import React, { useMemo } from 'react';
import { formatDateToCompanyFormat } from '../../utils/dateUtils';
import { buildBillPreviewCompanyDetails } from '../../utils/dataAdapter';
import { PreviewModal, MODAL_SECTIONS } from '../Modal';

const SalarySlip = ({ salaryPayment, currentCompany, onClose, onPrint, onPDF }) => {
  const company = useMemo(
    () => buildBillPreviewCompanyDetails(currentCompany || {}, {}),
    [currentCompany]
  );

  if (!salaryPayment) return null;

  const formatCurrency = (amount) => {
    return `RS ${new Intl.NumberFormat('en-IN', {
      minimumFractionDigits: 2
    }).format(amount)}`;
  };

  const formatDate = (dateString) => {
    return formatDateToCompanyFormat(dateString, company);
  };

  const companyName = company.firmName || company.name || 'Company Name';
  const companyAddress = company.address || 'Company Address';
  const companyCity = company.city;
  const companyState = company.state;
  const companyPincode = company.pincode;
  const companyGSTIN = company.gstin || company.gstNumber;
  const companyLogoUrl = company.logoUrl || '/acctoo-logo.png';

  // Get earnings from fixed and performance rows
  const getEarnings = () => {
    const earnings = [];
    
    // Add fixed salary components
    if (salaryPayment.fixedRows && Array.isArray(salaryPayment.fixedRows)) {
      salaryPayment.fixedRows.forEach(row => {
        if (row.type && row.amount && parseFloat(row.amount) > 0) {
          earnings.push({
            type: row.type,
            amount: parseFloat(row.amount),
            remark: row.remark || ''
          });
        }
      });
    }
    
    // Add performance salary components
    if (salaryPayment.performanceRows && Array.isArray(salaryPayment.performanceRows)) {
      salaryPayment.performanceRows.forEach(row => {
        if (row.type && row.amount && parseFloat(row.amount) > 0) {
          earnings.push({
            type: row.type,
            amount: parseFloat(row.amount),
            remark: row.remark || ''
          });
        }
      });
    }
    
    // Add irregular earnings
    if (salaryPayment.irregularApplied && Array.isArray(salaryPayment.irregularApplied)) {
      salaryPayment.irregularApplied.forEach(item => {
        if (item.appliedAs === 'Earning' && item.amount > 0) {
          earnings.push({
            type: item.paymentType || 'Bonus/Allowance',
            amount: parseFloat(item.amount),
            remark: 'Applied from advances'
          });
        }
      });
    }
    
    return earnings;
  };

  // Get deductions
  const getDeductions = () => {
    const deductions = [];
    
    // Add salary deductions
    if (salaryPayment.deductions && Array.isArray(salaryPayment.deductions)) {
      salaryPayment.deductions.forEach(deduction => {
        if (deduction.reason && deduction.amount && parseFloat(deduction.amount) > 0) {
          deductions.push({
            type: deduction.reason,
            amount: parseFloat(deduction.amount),
            remark: deduction.remark || ''
          });
        }
      });
    }
    
    // Add irregular deductions
    if (salaryPayment.irregularApplied && Array.isArray(salaryPayment.irregularApplied)) {
      salaryPayment.irregularApplied.forEach(item => {
        if (item.appliedAs === 'Deduction' && item.amount > 0) {
          deductions.push({
            type: item.paymentType || 'Advance Deduction',
            amount: parseFloat(item.amount),
            remark: 'Applied from advances'
          });
        }
      });
    }
    
    return deductions;
  };

  const earnings = getEarnings();
  const deductions = getDeductions();
  const totalEarnings = earnings.reduce((sum, comp) => sum + (parseFloat(comp.amount) || 0), 0);
  const totalDeductions = deductions.reduce((sum, comp) => sum + (parseFloat(comp.amount) || 0), 0);
  const netSalary = totalEarnings - totalDeductions;

  return (
    <PreviewModal
      section={MODAL_SECTIONS.SALARY}
      isOpen
      onClose={onClose}
      title="Salary slip"
      showPrintButton
      onPrint={onPrint}
      showPdfButton
      onPdf={onPDF}
      maxWidth="max-w-4xl"
      zIndex="z-[100]"
    >
        <div id="salary-slip-content" className="w-full mx-auto">
          {/* Company Header - Side by Side Layout */}
          <div className="flex items-start justify-between mb-4 border-b pb-3">
            {/* Company Logo - Left Side */}
            <div className="flex-shrink-0">
              <img 
                src={companyLogoUrl} 
                alt={`${companyName} Logo`}
                className="h-20 w-auto object-contain"
                onError={(e) => {
                  // Fallback to default logo if company logo fails to load
                  e.target.src = '/acctoo-logo.png';
                }}
              />
            </div>
            
            {/* Company Details - Right Side */}
            <div className="flex-1 text-right">
              <h1 className="text-xl font-bold text-gray-800 mb-2">
                {companyName}
              </h1>
              <div className="text-sm text-gray-600 space-y-1">
                {companyGSTIN && (
                  <div className="font-medium">GSTIN: {companyGSTIN}</div>
                )}
                <div>{companyAddress}</div>
                {companyCity && companyState && (
                  <div>{companyCity}, {companyState} - {companyPincode}</div>
                )}
              </div>
            </div>
          </div>

          {/* Employee Details & Pay Period - Compact 2 columns */}
          <div className="grid grid-cols-2 gap-4 mb-3">
            <div>
              <h3 className="font-semibold text-gray-800 mb-1 text-sm">Employee Details</h3>
              <div className="space-y-0 text-xs">
                <div><span className="font-medium">Name:</span> {salaryPayment.employeeName}</div>
                <div><span className="font-medium">ID:</span> {salaryPayment.employeeId}</div>
                <div><span className="font-medium">Designation:</span> {salaryPayment.post || 'N/A'}</div>
              </div>
            </div>
            <div>
              <h3 className="font-semibold text-gray-800 mb-1 text-sm">Pay Period</h3>
              <div className="space-y-0 text-xs">
                <div><span className="font-medium">Month:</span> {salaryPayment.month}</div>
                <div><span className="font-medium">Date:</span> {formatDate(salaryPayment.date)}</div>
                <div><span className="font-medium">Mode:</span> {salaryPayment.paymentMode || 'N/A'}</div>
              </div>
            </div>
          </div>

          {/* Salary Breakdown - Side by Side */}
          <div className="mb-3">
            <h3 className="font-semibold text-gray-800 mb-2 text-sm">Salary Breakdown</h3>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Earnings */}
              <div className="w-full">
                <h4 className="font-medium text-green-700 mb-1 text-xs">Earnings</h4>
                <div className="border border-gray-200 rounded text-xs overflow-hidden">
                  <table className="w-full table-fixed border-collapse">
                    <colgroup>
                      <col className="w-1/2" />
                      <col className="w-1/4" />
                      <col className="w-1/4" />
                    </colgroup>
                    <thead>
                      <tr className="bg-gray-50">
                        <th className="px-2 py-1 text-left font-semibold">Component</th>
                        <th className="px-2 py-1 text-right font-semibold">Amount</th>
                        <th className="px-2 py-1 font-semibold">Remark</th>
                      </tr>
                    </thead>
                    <tbody>
                      {earnings.map((comp, index) => (
                        <tr key={index} className="border-b align-top">
                          <td className="px-2 py-1 whitespace-normal break-words align-top" title={comp.type}>{comp.type}</td>
                          <td className="px-2 py-1 text-right font-medium align-top" style={{ fontVariantNumeric: 'tabular-nums' }}>{formatCurrency(parseFloat(comp.amount) || 0)}</td>
                          <td className="px-2 py-1 whitespace-normal break-words text-gray-600 align-top" title={comp.remark || '-'}>{comp.remark || '-'}</td>
                        </tr>
                      ))}
                      <tr className="bg-green-50">
                        <td className="px-2 py-1 font-semibold">Total Earnings</td>
                        <td className="px-2 py-1 text-right font-semibold">{formatCurrency(totalEarnings)}</td>
                        <td className="px-2 py-1"></td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Deductions */}
              <div className="w-full">
                <h4 className="font-medium text-red-700 mb-1 text-xs">Deductions</h4>
                <div className="border border-gray-200 rounded text-xs overflow-hidden">
                  <table className="w-full table-fixed border-collapse">
                    <colgroup>
                      <col className="w-1/2" />
                      <col className="w-1/4" />
                      <col className="w-1/4" />
                    </colgroup>
                    <thead>
                      <tr className="bg-gray-50">
                        <th className="px-2 py-1 text-left font-semibold">Component</th>
                        <th className="px-2 py-1 text-right font-semibold">Amount</th>
                        <th className="px-2 py-1 font-semibold">Remark</th>
                      </tr>
                    </thead>
                    <tbody>
                      {deductions.map((comp, index) => (
                        <tr key={index} className="border-b align-top">
                          <td className="px-2 py-1 whitespace-normal break-words align-top" title={comp.type || 'Deduction'}>{comp.type || 'Deduction'}</td>
                          <td className="px-2 py-1 text-right font-medium align-top" style={{ fontVariantNumeric: 'tabular-nums' }}>{formatCurrency(parseFloat(comp.amount) || 0)}</td>
                          <td className="px-2 py-1 whitespace-normal break-words text-gray-600 align-top" title={comp.remark || '-'}>{comp.remark || '-'}</td>
                        </tr>
                      ))}
                      <tr className="bg-red-50">
                        <td className="px-2 py-1 font-semibold">Total Deductions</td>
                        <td className="px-2 py-1 text-right font-semibold">{formatCurrency(totalDeductions)}</td>
                        <td className="px-2 py-1"></td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

            {/* Net Salary - Compact */}
            <div className="bg-acctoo-50 p-2 rounded mt-2">
              <div className="flex justify-between items-center">
                <span className="text-sm font-semibold text-gray-800">Net Salary</span>
                <span className="text-lg font-bold text-acctoo-700">{formatCurrency(netSalary)}</span>
              </div>
            </div>
          </div>

          {/* Footer - Compact */}
          <div className="text-center text-xs text-gray-500 border-t pt-2">
            <p>This is a computer generated salary slip and does not require signature.</p>
            <p>Generated on: {new Date().toLocaleDateString('en-IN')}</p>
            <p className="mt-1 font-medium text-orange-600">ACCTOO "Simplifying Accounting, Together"</p>
          </div>
        </div>
    </PreviewModal>
  );
};

export default SalarySlip;
